import { JSX, useState } from 'react';
import { t } from 'i18next';
import { Box, ClickAwayListener, LinearProgress, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { EStatus, ECellType, TableHeaders } from '../../data/Constants';

/**
 * Renders a linear progress bar with a label showing the count of successful activities
 * @param {string | null} activities - JSON string containing activities and their statuses
 * @param {Function} getStatusIcon - Function to render status icons based on status value
 * @returns {React.JSX.Element} Linear progress bar component with completion ratio label
 */

export const LinearProgressBar = ({
  activities,
  getStatusIcon,
}: {
  activities: string | null;
  getStatusIcon: (status: string, fontSize?: 'small' | 'medium' | 'inherit') => JSX.Element | null;
}) => {
  const [open, setOpen] = useState(false);

  // If no activity available show empty cell
  if (!activities) {
    return null;
  }

  let value = 0;
  let totalActivitiesCount = 0;
  let successActivities = 0;
  let parsedActivites: Record<string, string> | null = null;
  if (activities) {
    parsedActivites = JSON.parse(activities);
    totalActivitiesCount = Object.keys(parsedActivites!).length;
    successActivities = Object.values(parsedActivites!).filter(
      (value) => ![EStatus.New, EStatus.Initial, EStatus.Processing].includes(value)
    ).length;
    value = (successActivities / totalActivitiesCount) * 100;
  }

  const rows = parsedActivites
    ? Object.entries(parsedActivites).map(([activities, status], index) => {
        const translatedActivity = t('labels.' + activities);
        return {
          id: index,
          activities:
            translatedActivity === 'labels.' + activities ? activities : translatedActivity, //If translation of activity available show from translation else show activity
          status,
        };
      })
    : [];

  const columns: GridColDef[] = TableHeaders.releaseActivity.main.map((header) => ({
    field: header.field,
    headerName: t('labels.' + header.field),
    flex: header.flex,
    width: header.width,
    sortable: header.sort,
    filterable: header.filter,
    ...(header.type === ECellType.Status && {
      renderCell: (params: GridRenderCellParams) => getStatusIcon(params.value, 'small'),
    }),
  }));

  const tooltipContent = parsedActivites ? (
    <Box sx={{ width: 170, maxHeight: 250 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        columnHeaderHeight={32}
        rowHeight={30}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-cell': {
            padding: '4px 0px 0px 20px;',
          },
          border: 'none',
        }}
      />
    </Box>
  ) : (
    ''
  );

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        title={tooltipContent}
        arrow
        placement="bottom"
        open={open}
        disableHoverListener
        disableFocusListener
        disableTouchListener
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: '#fff',
              color: '#000',
              boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid #e0e0e0',
            },
          },
          arrow: {
            sx: {
              color: '#fff',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            cursor: parsedActivites ? 'pointer' : 'default',
          }}
          className="h-inherit"
          onClick={() => parsedActivites && setOpen(!open)}
        >
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress
              color="success"
              variant="determinate"
              value={value}
              sx={{
                backgroundColor: '#eeeeee',
              }}
            />
          </Box>
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {successActivities}/{totalActivitiesCount}
            </Typography>
          </Box>
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};
